import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack,
  Button, IconButton, Tooltip, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Slider, Alert,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import {
  loadThreatIntel, addThreatFeed, toggleThreatFeed, runFeedSync,
  addIoc, removeIoc, addWatchlistKeyword, removeWatchlistKeyword,
  runDarkWebScan, setDarkWebMentionStatus,
} from './threatIntelSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';
import useCurrentPermissions from '../../hooks/useCurrentPermissions';

const SEV_COLOR = { low: tokens.severity.low, medium: tokens.severity.medium, high: tokens.severity.high, critical: tokens.severity.critical };
const FEED_STATUS_COLOR = { active: tokens.severity.low, degraded: tokens.severity.medium };
const FEED_TYPES = ['OSINT', 'CISA_KEV', 'COMMERCIAL', 'INTERNAL', 'CUSTOM'];
const IOC_TYPES = ['IP', 'DOMAIN', 'URL', 'FILE_HASH', 'EMAIL'];
const SEVERITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const WATCHLIST_CATEGORIES = ['brand', 'domain', 'email', 'credential'];
const MENTION_STATUSES = ['new', 'reviewed', 'dismissed'];

const emptyFeed = { name: '', feed_type: 'OSINT', format: 'JSON_LINES', url: '', sync_interval_minutes: 360, enabled: true };
const emptyIoc = { ioc_type: 'IP', value: '', severity: 'MEDIUM', confidence: 60, description: '' };
const emptyKeyword = { keyword: '', category: 'brand' };

export default function ThreatIntelPage() {
  const dispatch = useDispatch();
  const { feeds, iocs, darkWeb, watchlist } = useSelector((s) => s.threatIntel);
  const { can, loaded } = useCurrentPermissions();
  const canWrite = can('threat:write');

  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [feedForm, setFeedForm] = useState(emptyFeed);
  const [iocDialogOpen, setIocDialogOpen] = useState(false);
  const [iocForm, setIocForm] = useState(emptyIoc);
  const [keywordForm, setKeywordForm] = useState(emptyKeyword);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => { dispatch(loadThreatIntel()); }, [dispatch]);

  const totalIocs = feeds.reduce((sum, f) => sum + f.iocCount, 0);
  const criticalIocs = iocs.filter((i) => i.severity === 'critical').length;

  const runAction = async (thunk, arg) => {
    if (!canWrite) { setError('Your role has read-only Threat Intelligence access. Ask an administrator for threat:write permission.'); return false; }
    setError(null);
    const result = await dispatch(thunk(arg));
    if (thunk.rejected.match(result)) {
      setError(result.error?.message || 'That action could not be completed.');
      return false;
    }
    return true;
  };

  const handleCreateFeed = async () => {
    if (!feedForm.name.trim()) return setError('Feed name is required.');
    const ok = await runAction(addThreatFeed, feedForm);
    if (ok) { setFeedDialogOpen(false); setFeedForm(emptyFeed); }
  };

  const handleCreateIoc = async () => {
    if (!iocForm.value.trim()) return setError('IOC value is required.');
    const ok = await runAction(addIoc, iocForm);
    if (ok) { setIocDialogOpen(false); setIocForm(emptyIoc); }
  };

  const handleAddKeyword = async () => {
    if (!keywordForm.keyword.trim()) return setError('Keyword is required.');
    const ok = await runAction(addWatchlistKeyword, keywordForm);
    if (ok) setKeywordForm(emptyKeyword);
  };

  const handleRunScan = async () => {
    setScanning(true);
    await runAction(runDarkWebScan);
    setScanning(false);
  };

  return (
    <Box>
      <PageHeader
        title="Threat Intelligence"
        subtitle="IP reputation, IOC matching, dark web monitoring, and external threat feeds"
        action={<ModuleControlPanel basePath="/threat-intel" moduleLabel="Threat Intelligence" />}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loaded && !canWrite && <Alert severity="info" sx={{ mb: 2 }}>Your role has read-only Threat Intelligence access. Ask an administrator for threat:write permission to add or change feeds, IOCs, watchlists, scans, or mention status.</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={3}><StatCard label="Active Feeds" value={feeds.filter((f) => f.status === 'active').length} icon={PublicRoundedIcon} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Total IOCs Tracked" value={totalIocs.toLocaleString()} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Recent Critical Matches" value={criticalIocs} accent={tokens.severity.critical} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Dark Web Mentions" value={darkWeb.length} accent={tokens.severity.medium} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Threat Feeds</Typography>
              <Button size="small" startIcon={<AddRoundedIcon />} disabled={!canWrite} onClick={() => setFeedDialogOpen(true)}>Add Feed</Button>
            </Box>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {feeds.map((f) => (
                <Box key={f.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.2, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.82rem', color: tokens.text.primary }}>{f.name}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted }}>{f.category} · {f.iocCount.toLocaleString()} IOCs · every {f.sync_interval_minutes}m</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip label={f.status} size="small" sx={{ color: FEED_STATUS_COLOR[f.status], border: `1px solid ${FEED_STATUS_COLOR[f.status]}`, backgroundColor: 'transparent' }} />
                    <Tooltip title="Sync now">
                      <IconButton size="small" disabled={!canWrite} onClick={() => runAction(runFeedSync, f.id)}><SyncRoundedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={f.enabled ? 'Disable feed' : 'Enable feed'}>
                      <Switch size="small" disabled={!canWrite} checked={f.enabled} onChange={() => runAction(toggleThreatFeed, { id: f.id, enabled: !f.enabled })} />
                    </Tooltip>
                  </Stack>
                </Box>
              ))}
              {!feeds.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, textAlign: 'center', py: 2 }}>No threat feeds configured yet.</Typography>}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Watchlist Keywords</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: tokens.text.muted, mb: 1.5 }}>Brand, domain, email, or credential terms to monitor for dark web exposure.</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <TextField disabled={!canWrite} size="small" placeholder="e.g. mycompany.com" value={keywordForm.keyword}
                onChange={(e) => setKeywordForm((f) => ({ ...f, keyword: e.target.value }))} sx={{ flex: 1 }} />
              <TextField disabled={!canWrite} select size="small" value={keywordForm.category}
                onChange={(e) => setKeywordForm((f) => ({ ...f, category: e.target.value }))} sx={{ width: 130 }}>
                {WATCHLIST_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <Button variant="contained" disabled={!canWrite} onClick={handleAddKeyword}>Add</Button>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
              {watchlist.map((k) => (
                <Chip
                  key={k.id} label={`${k.keyword} (${k.category})`} size="small"
                  onDelete={canWrite ? () => runAction(removeWatchlistKeyword, k.id) : undefined}
                  sx={{ color: tokens.text.secondary, border: `1px solid ${tokens.border}`, backgroundColor: 'transparent' }}
                />
              ))}
              {!watchlist.length && <Typography sx={{ fontSize: '0.75rem', color: tokens.text.muted }}>No watchlist keywords yet — add one above to enable dark web scanning.</Typography>}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Dark Web Mentions</Typography>
              <Button size="small" startIcon={<TravelExploreRoundedIcon />} onClick={handleRunScan} disabled={!canWrite || scanning || !watchlist.length}>
                {scanning ? 'Scanning…' : 'Run Scan'}
              </Button>
            </Box>
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {darkWeb.map((d) => (
                <Box key={d.id} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary }}>{d.title}</Typography>
                    <TextField select size="small" disabled={!canWrite} value={d.status} variant="standard"
                      onChange={(e) => runAction(setDarkWebMentionStatus, { id: d.id, status: e.target.value })}
                      sx={{ minWidth: 96 }}>
                      {MENTION_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </TextField>
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted }}>{d.source_label || d.source} · confidence: {d.confidence ?? '—'} · {formatRelativeTime(d.discovered_at || d.detectedAt)}</Typography>
                </Box>
              ))}
              {!darkWeb.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, textAlign: 'center', py: 2 }}>No dark web mentions found yet.</Typography>}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>IOC Matches</Typography>
              <Button size="small" startIcon={<AddRoundedIcon />} disabled={!canWrite} onClick={() => setIocDialogOpen(true)}>Add IOC</Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Type', 'Value', 'Severity', 'Matched Against', 'Seen', ''].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {iocs.map((i) => (
                  <TableRow key={i.id} hover>
                    <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{i.type || i.ioc_type}</TableCell>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.72rem', maxWidth: 240 }}>{i.value}</TableCell>
                    <TableCell><Chip label={i.severity} size="small" sx={{ color: SEV_COLOR[i.severity], border: `1px solid ${SEV_COLOR[i.severity]}`, backgroundColor: 'transparent' }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.secondary }}>{i.matchedAgainst || i.description || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(i.firstSeen || i.first_seen)}</TableCell>
                    <TableCell>
                      <Tooltip title="Deactivate">
                        <IconButton size="small" disabled={!canWrite} onClick={() => runAction(removeIoc, i.id)}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!iocs.length && (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>No IOCs tracked yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={feedDialogOpen} onClose={() => setFeedDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Threat Feed</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" size="small" value={feedForm.name} onChange={(e) => setFeedForm((f) => ({ ...f, name: e.target.value }))} autoFocus />
            <TextField select label="Feed Type" size="small" value={feedForm.feed_type} onChange={(e) => setFeedForm((f) => ({ ...f, feed_type: e.target.value }))}>
              {FEED_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="URL" size="small" value={feedForm.url} onChange={(e) => setFeedForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://..." />
            <TextField label="Sync Interval (minutes)" type="number" size="small" value={feedForm.sync_interval_minutes}
              onChange={(e) => setFeedForm((f) => ({ ...f, sync_interval_minutes: Number(e.target.value) }))} inputProps={{ min: 5 }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFeedDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!canWrite} onClick={handleCreateFeed}>Add Feed</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={iocDialogOpen} onClose={() => setIocDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add IOC</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select label="Type" size="small" value={iocForm.ioc_type} onChange={(e) => setIocForm((f) => ({ ...f, ioc_type: e.target.value }))}>
              {IOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Value" size="small" value={iocForm.value} onChange={(e) => setIocForm((f) => ({ ...f, value: e.target.value }))} autoFocus placeholder="e.g. 198.51.100.23" />
            <TextField select label="Severity" size="small" value={iocForm.severity} onChange={(e) => setIocForm((f) => ({ ...f, severity: e.target.value }))}>
              {SEVERITIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <Box>
              <Typography variant="caption" sx={{ color: tokens.text.muted }}>Confidence ({iocForm.confidence})</Typography>
              <Slider value={iocForm.confidence} onChange={(_, v) => setIocForm((f) => ({ ...f, confidence: v }))} min={0} max={100} />
            </Box>
            <TextField label="Description" size="small" multiline minRows={2} value={iocForm.description} onChange={(e) => setIocForm((f) => ({ ...f, description: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setIocDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!canWrite} onClick={handleCreateIoc}>Add IOC</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
